import {createContext, memo, useContext, useMemo} from 'react'

import type {PaginatedTotalCount} from '../../api/memex-items/paginated-views'
import {getGroupingMetadataFromServerGroupValue} from '../../features/grouping/get-grouping-metadata'
import type {GroupingMetadataWithSource} from '../../features/grouping/types'
import {useSliceBy} from '../../features/slicing/hooks/use-slice-by'
import {useSliceByDataQuery} from '../../state-providers/memex-items/queries/use-slice-by-data-query'

export type SlicerItemGroup = GroupingMetadataWithSource & {totalCount: PaginatedTotalCount}

export type SlicerItemsContextValue = {
  slicerItems: Array<SlicerItemGroup> | null
}

const SlicerItemsContext = createContext<SlicerItemsContextValue>({slicerItems: null})

export const useSlicerItems = () => {
  const contextValue = useContext(SlicerItemsContext)

  return contextValue
}

export const SlicerItemsProvider = memo<{children: React.ReactNode}>(function SlicerItemsProvider({children}) {
  const sliceBy = useSliceBy()
  const {data: sliceByQueryData} = useSliceByDataQuery()
  const slicerItems: Array<SlicerItemGroup> = useMemo(() => {
    if (!sliceBy.sliceField || !sliceByQueryData) return []

    // Assuming we have a slice by field and data in the sliceByQuery,
    // we want to iterate over the slices and return the SlicerItemGroup
    // expected by the slice by panel
    const columnModel = sliceBy.sliceField
    return (
      sliceByQueryData.slices
        .map(sliceData => {
          const {sliceValue, sliceMetadata, totalCount} = sliceData
          const groupMetadataFromServerValue = getGroupingMetadataFromServerGroupValue(
            columnModel,
            sliceValue,
            sliceMetadata,
          )

          if (!groupMetadataFromServerValue) {
            return undefined
          }

          return {
            sourceObject: groupMetadataFromServerValue,
            value: sliceValue,
            totalCount,
          }
        })
        // if we were unable to find grouping metadata for the sliceValue, the map call above will
        // return undefined, so we want to filter those values out.
        .filter((g): g is SlicerItemGroup => !!g)
    )
  }, [sliceBy.sliceField, sliceByQueryData])

  const ctx = useMemo(
    () => ({
      slicerItems,
    }),

    [slicerItems],
  )

  return <SlicerItemsContext value={ctx}>{children}</SlicerItemsContext>
})

try{ SlicerItemsContext.displayName ||= 'SlicerItemsContext' } catch {}
try{ SlicerItemsProvider.displayName ||= 'SlicerItemsProvider' } catch {}